import { useMutation } from '@tanstack/react-query';

import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import {
  createTextQuestion,
  updateTextQuestion,
} from '@/data/services/question/textQuestionServices';
import Activity from '@/models/Activity';
import { Question, TextQuestion } from '@/models/Question';
import {
  adapterFeedbacks,
  sanitizeFeedbacks,
} from '@/utils/questions/adapterFeedbacks';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import { isEqual } from 'lodash';
import MultimediaForm, { MultimediaFormFields } from './MultimediaForm';

type MultimediaFormServiceProps = {
  activity: Activity;
  question: Question;
  isActive?: boolean;
};

export default function MultimediaFormService({
  activity,
  question,
  isActive,
}: MultimediaFormServiceProps) {
  const gradeCtx = useQuestionExamGradeCtx();

  const handleCreate = async (
    data: MultimediaFormFields,
  ): Promise<TextQuestion> => {
    const { maxGrade, feedbacks, ...rest } = data;
    const saved = await createTextQuestion({
      activity: activity.id,
      type: question.type,
      identifier: question.identifier,
      order: question.order,
      feedbacks: sanitizeFeedbacks(feedbacks),
      ...rest,
    });
    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const handleUpdate = async (
    data: MultimediaFormFields,
  ): Promise<TextQuestion> => {
    const { maxGrade, ...rest } = data;
    const oldValues: Omit<MultimediaFormFields, 'maxGrade'> = {
      title: normalizeTranslatableField(question.title),
      context: normalizeTranslatableField(question.context),
      feedbacks: adapterFeedbacks(question.feedbacks ?? []),
    };

    const changedFields: Partial<Omit<MultimediaFormFields, 'maxGrade'>> = {};
    if (!isEqual(oldValues.title, rest.title)) {
      changedFields.title = rest.title;
    }
    if (!isEqual(oldValues.context, rest.context)) {
      changedFields.context = rest.context;
    }
    const newSanitizedFeedbacks = sanitizeFeedbacks(rest.feedbacks);
    const oldSanitizedFeedbacks = sanitizeFeedbacks(oldValues.feedbacks);
    if (!isEqual(oldSanitizedFeedbacks, newSanitizedFeedbacks)) {
      changedFields.feedbacks = newSanitizedFeedbacks;
    }

    const saved =
      Object.keys(changedFields).length === 0
        ? (question as TextQuestion)
        : await updateTextQuestion(question.id, changedFields);

    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: (data: MultimediaFormFields) =>
      !question.id ? handleCreate(data) : handleUpdate(data),
    onError: err => alert.error(getErrorMessage(err)),
  });

  return (
    <MultimediaForm
      key={question.identifier}
      isActive={isActive}
      question={question}
      onSubmit={submitQuestion}
      isLoading={isPending}
    />
  );
}
